import {ChangeEvent, FC, useCallback, useEffect, useMemo} from 'react';
import {Box} from '@mui/material';
import dayjs from 'dayjs';
import {
    activeFlagOf,
    ChargingSessionsTable,
    SessionStatus,
} from 'src/components/chargingSessions/ChargingSessionsTable';
import {SessionStatsStrip} from 'src/components/chargingSessions/SessionStatsStrip';
import {transactionsApi} from 'src/api/transactions';
import {useAppDispatch, useAppSelector, useBusinessId} from 'src/app/hooks';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {useLiveSessionRefresh} from 'src/hooks/useLiveRefresh';
import {DateFilterType} from 'src/components/common/TimeSelect/TimeSelect.types';
import {store} from 'src/app/store';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {useCan, usePersona} from 'src/permissions/hooks';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening a session and coming back restores the view instead of resetting it.
 *
 * `preset` is the period select's own index and was already arriving from the reports page as
 * `?preset=3`, which is why it keeps that name. `from`/`to` are the window actually asked for: left
 * at 0 they mean "whatever the preset computes", so an untouched list carries no dates in its address
 * and a hand-picked range carries exactly the one that was picked. The defaults cannot hold "now"
 * anyway - this object has to be the same object on every render.
 *
 * The ids travel as numbers with 0 for "not set", because that is what the controls hand back and
 * what the request leaves out again.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    preset: 3,
    from: 0,
    to: 0,
    locationId: 0,
    chargerId: 0,
    userId: 0,
    userType: '',
    companyId: 0,
    tagId: 0,
    basis: 'start' as string,
    status: 'all' as string,
};

const computeDatesFromPreset = (preset: number): {from: number; to: number} => {
    const to = dayjs.utc().valueOf();
    switch (preset) {
        case 1: return {from: dayjs.utc().subtract(1, 'week').valueOf(), to};
        case 2: return {from: dayjs.utc().subtract(1, 'month').valueOf(), to};
        case 3: return {from: dayjs.utc().startOf('year').valueOf(), to};
        case 4: return {from: dayjs.utc('2020-01-01').valueOf(), to};
        default: return {from: dayjs.utc().startOf('year').valueOf(), to};
    }
};

export const DetailedChargingSessions: FC = () => {
    const {authInfo} = useAppSelector((state) => state.common);
    const [trigger, response] = transactionsApi.useLazyGetTransactionsQuery();

    // `roles[0].role` was the legacy claim and carried a role name; it no longer exists, so both of
    // these had been silently false and the page was showing every filter to a driver.
    const persona = usePersona();
    const isEmployeeUser = persona.isEmployee;
    const isPrivateDriver = persona.isDriver;
    const isSuperAdmin = useCan('reports.readPlatform');
    // Was a constant 1 for the platform administrator: the business being worked in now.
    const effectiveBusinessId = useBusinessId();

    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(authInfo && Object.keys(authInfo).length);
    const ownCompanyId = authInfo?.company?.id;
    const ownEmployeeId = authInfo?.employee?.id;

    /**
     * The window the list is asked for.
     *
     * A hand-picked range wins; otherwise the preset computes one. Memoised on those three fields and
     * nothing else, because the computation reads the clock - recomputed on every render it would be
     * a new object each time, and the effect below would ask the server for ever.
     */
    const dates = useMemo(
        () => (query.from && query.to
            ? {from: query.from, to: query.to}
            : computeDatesFromPreset(query.preset)),
        [query.from, query.to, query.preset]
    );

    const status = query.status as SessionStatus;
    const dateFilterType = query.basis as DateFilterType;
    const locationId = query.locationId || undefined;
    const chargerId = query.chargerId || undefined;
    // Not seeded from the account: a self-scoped caller has their own userId forced onto the request
    // by the server, and seeding it here only made the client look like the thing deciding.
    const userId = query.userId || undefined;
    const userType = (query.userType || undefined) as 'COMPANY' | 'PRIVATE_DRIVER' | undefined;
    const selectedCompanyId = query.companyId || undefined;
    const tagId = query.tagId || undefined;
    const searchValue = query.keyword;

    // Running sessions have no stop time, so the active tab always reads by start - without
    // forgetting the reader's choice for the other two tabs.
    const basis: DateFilterType = status === 'active' ? 'start' : dateFilterType;

    const userParams = useMemo(() => {
        if (userType === 'COMPANY' && selectedCompanyId) {
            return {companyId: selectedCompanyId};
        }
        if (userId) {
            return {userId};
        }
        return {};
    }, [userType, selectedCompanyId, userId]);

    // The list's own body without the status, which is what the tiles split it by. Read by the reader's
    // basis: the server counts the running sessions by start on its own.
    const statsRequest = useMemo(() => ({
        businessId: effectiveBusinessId,
        companyId: ownCompanyId,
        employeeId: ownEmployeeId,
        keyword: searchValue?.length ? searchValue : undefined,
        ...(dateFilterType === 'stop'
            ? {stopFrom: dates.from, stopTo: dates.to}
            : {startFrom: dates.from, startTo: dates.to}),
        locationId,
        chargerId,
        ...userParams,
        tagId,
    }), [
        effectiveBusinessId, ownCompanyId, ownEmployeeId, searchValue, dateFilterType, dates,
        locationId, chargerId, userParams, tagId,
    ]);
    const {data: sessionStats} = transactionsApi.useGetTransactionStatsQuery(statsRequest, {
        skip: !isSignedIn,
    });

    // A session that started or ended a minute ago is not a number worth showing. Both the tiles and
    // the rows carry the `Transactions` tag, so one invalidation brings them back together - quietly,
    // with no preloader and no toast, because nobody asked for this refresh.
    const dispatch = useAppDispatch();
    useLiveSessionRefresh(() => {
        dispatch(transactionsApi.util.invalidateTags(['Transactions']));
    });

    /** The whole request body, built from the address bar and from nothing else. */
    const listRequest = useMemo(() => ({
        businessId: effectiveBusinessId,
        companyId: ownCompanyId,
        employeeId: ownEmployeeId,
        keyword: searchValue?.length ? searchValue : undefined,
        active: activeFlagOf(status),
        ...(basis === 'stop'
            ? {stopFrom: dates.from, stopTo: dates.to}
            : {startFrom: dates.from, startTo: dates.to}),
        locationId,
        chargerId,
        ...userParams,
        tagId,
    }), [
        effectiveBusinessId, ownCompanyId, ownEmployeeId, searchValue, status, basis, dates,
        locationId, chargerId, userParams, tagId,
    ]);

    const fetchData = useCallback(async () => {
        store.dispatch(preloaderChangeStatus(true));
        try {
            await trigger({
                params: {page: query.page, size: query.size},
                data: listRequest,
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            store.dispatch(preloaderChangeStatus(false));
        }
    }, [trigger, listRequest, query.page, query.size]);

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler below only writes the URL. Before this each of them called the server itself with
     * the values it was about to set, and the period was read once on mount from `?preset=` into
     * `useState` - so a Back from a session, which arrives with the whole view already in the URL,
     * fetched the unfiltered list on page one.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void fetchData();
    }, [isSignedIn, fetchData]);

    const handleChangePage = (newPage: number) => setQuery((prev) => ({...prev, page: newPage}));

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery((prev) => ({
        ...prev,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    }));

    const handleNameChange = () => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery((prev) => ({...prev, page: 0, keyword: value}));
    };

    /**
     * The period select hands over its index and the dates it computed through two callbacks, one
     * after the other. Both write through the updater form, so the second does not put the first
     * field back as it was: picking a preset clears the hand-picked window, and a hand-picked window
     * keeps the index the select is showing.
     */
    const handleSetDates = (from: number, to: number, _field: string) => {
        return () => setQuery((prev) => ({...prev, page: 0, from, to}));
    };

    const handleChangeCurTime = (index: number) => setQuery((prev) => ({
        ...prev,
        page: 0,
        preset: index,
        from: 0,
        to: 0,
    }));

    const handleLocationChange = (newLocationId: number | null) => {
        setQuery((prev) => ({
            ...prev,
            page: 0,
            locationId: newLocationId ?? 0,
            // Reset charger and tag when location changes.
            chargerId: 0,
            tagId: 0,
            // For EMPLOYEE and PRIVATE_DRIVER, keep their own user filter: it is not theirs to set,
            // and the server decides it anyway.
            ...(isEmployeeUser || isPrivateDriver ? {} : {userId: 0, userType: '', companyId: 0}),
        }));
    };

    const handleChargerChange = (newChargerId: number | null) => setQuery((prev) => ({
        ...prev,
        page: 0,
        chargerId: newChargerId ?? 0,
    }));

    const handleUserChange = (
        newUserId: number | null,
        newUserType?: 'COMPANY' | 'PRIVATE_DRIVER',
        newCompanyId?: number
    ) => {
        // EMPLOYEE and PRIVATE_DRIVER cannot change the user filter - they only see their own sessions.
        if (isEmployeeUser || isPrivateDriver) {
            return;
        }
        setQuery((prev) => ({
            ...prev,
            page: 0,
            userId: newUserId ?? 0,
            userType: newUserType ?? '',
            companyId: newCompanyId ?? 0,
        }));
    };

    /**
     * Every other filter resets the page, and this one changes the result set just as much: a session
     * that started in March and stopped in April moves in and out of the period. Left on page five,
     * the switch can land on a page the new result set does not have.
     */
    const handleDateFilterTypeChange = (type: DateFilterType) => setQuery((prev) => ({
        ...prev,
        page: 0,
        basis: type,
    }));

    // Clicking the chosen tile again goes back to all sessions, as on every other list's block.
    const handleStatusChange = (newStatus: SessionStatus) => setQuery((prev) => ({
        ...prev,
        page: 0,
        status: prev.status === newStatus ? 'all' : newStatus,
    }));

    const handleTagChange = (newTagId: number | null) => setQuery((prev) => ({
        ...prev,
        page: 0,
        tagId: newTagId ?? 0,
    }));

    const transactions = response.data?.content || [];
    const totalPages = response.data?.page?.totalPages || 0;
    const totalElements = response.data?.page?.totalElements;

    return (
        <Box>
            {/* The page adds nothing at the sides below a desktop: the layout's column is already the
                width of the screen, and 24px here put the list back inside a margin the shell had
                just given up. */}
            <Box sx={{p: {xs: 0, md: 3}, display: 'flex', flexDirection: 'column', gap: 2}}>
                <SessionStatsStrip
                    data={sessionStats}
                    activeStatus={status}
                    onSelectStatus={handleStatusChange}
                />
                <ChargingSessionsTable
                    data={transactions}
                    page={query.page}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    perPage={query.size}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    handleChangePage={handleChangePage}
                    handleNameChange={handleNameChange}
                    searchValue={searchValue}
                    handleSetDates={handleSetDates}
                    handleChangeCurTime={handleChangeCurTime}
                    curTime={query.preset}
                    locationId={locationId}
                    chargerId={chargerId}
                    userId={userId}
                    userType={userType}
                    selectedCompanyId={selectedCompanyId}
                    tagId={tagId}
                    onLocationChange={handleLocationChange}
                    onChargerChange={handleChargerChange}
                    onUserChange={handleUserChange}
                    onTagChange={handleTagChange}
                    hideUserFilter={isEmployeeUser || isPrivateDriver}
                    hideAllFiltersExceptDate={isEmployeeUser || isPrivateDriver}
                    startFrom={dates.from}
                    startTo={dates.to}
                    dateFilterType={dateFilterType}
                    onDateFilterTypeChange={handleDateFilterTypeChange}
                    status={status}
                />
            </Box>
        </Box>
    );
};
